import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import SellRoundedIcon from "@mui/icons-material/SellRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { getCategories, createCategory, updateCategory, deleteCategory } from "../../api/adminService";

const LEVELS = [
  {
    level: 1,
    title: "Main Categories",
    singular: "Main Category",
    icon: FolderRoundedIcon,
    emptyText: "No main categories yet",
    emptyHint: "Create your first top-level department.",
  },
  {
    level: 2,
    title: "Subcategories",
    singular: "Subcategory",
    icon: AccountTreeRoundedIcon,
    emptyText: "No subcategories yet",
    emptyHint: "Select a main category, then add one.",
  },
  {
    level: 3,
    title: "Items",
    singular: "Item",
    icon: SellRoundedIcon,
    emptyText: "No items yet",
    emptyHint: "Select a subcategory, then add one.",
  },
];

export default function Categories() {
  const [tree, setTree] = useState({ 1: [], 2: [], 3: [] });
  const [loading, setLoading] = useState({ 1: true, 2: false, 3: false });
  const [selected, setSelected] = useState({ 1: null, 2: null });
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null); // { level, parentCategoryId, parentName, category? }
  const [deleteTarget, setDeleteTarget] = useState(null); // { level, category }

  async function reloadLevel(level, parentCategoryId) {
    setLoading((prev) => ({ ...prev, [level]: true }));
    try {
      const params = { level };
      if (level > 1) params.parentCategoryId = parentCategoryId;
      const data = await getCategories(params);
      setTree((prev) => ({ ...prev, [level]: data }));
    } finally {
      setLoading((prev) => ({ ...prev, [level]: false }));
    }
  }

  useEffect(() => {
    reloadLevel(1).catch(() => setError("Could not load categories. Is the API running?"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selected[1]) {
      reloadLevel(2, selected[1]).catch(() => setError("Could not load subcategories."));
    } else {
      setTree((prev) => ({ ...prev, 2: [], 3: [] }));
    }
  }, [selected[1]]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selected[2]) {
      reloadLevel(3, selected[2]).catch(() => setError("Could not load items."));
    } else {
      setTree((prev) => ({ ...prev, 3: [] }));
    }
  }, [selected[2]]); // eslint-disable-line react-hooks/exhaustive-deps

  function refreshAll() {
    reloadLevel(1).catch(() => setError("Could not load categories. Is the API running?"));
    if (selected[1]) reloadLevel(2, selected[1]).catch(() => setError("Could not load subcategories."));
    if (selected[2]) reloadLevel(3, selected[2]).catch(() => setError("Could not load items."));
  }

  function openCreateDialog(level) {
    const parentCategoryId = level === 1 ? null : selected[level - 1];
    const parentName = level === 1 ? null : tree[level - 1].find((c) => c.categoryId === parentCategoryId)?.name;
    setDialog({ level, parentCategoryId, parentName, category: null });
  }

  function openEditDialog(level, category) {
    const parentName =
      level === 1 ? null : tree[level - 1].find((c) => c.categoryId === category.parentCategoryId)?.name;
    setDialog({ level, parentCategoryId: category.parentCategoryId, parentName, category });
  }

  async function handleSave(values) {
    const { level, parentCategoryId, category } = dialog;
    const payload = {
      name: values.name,
      parentCategoryId: parentCategoryId ?? null,
      imageUrl: values.imageUrl || null,
      displayOrder: Number(values.displayOrder) || 0,
      isActive: values.isActive,
    };

    try {
      if (category) {
        await updateCategory(category.categoryId, payload);
      } else {
        await createCategory(payload);
      }
      setDialog(null);
      const parentForLevel = level === 1 ? undefined : parentCategoryId;
      await reloadLevel(level, parentForLevel);
    } catch (err) {
      setError(err.response?.data || "Could not save this category.");
      throw err;
    }
  }

  async function confirmDelete() {
    const { level, category } = deleteTarget;
    try {
      await deleteCategory(category.categoryId);
      const parentForLevel = level === 1 ? undefined : category.parentCategoryId;
      await reloadLevel(level, parentForLevel);
      if (level < 3) setSelected((prev) => ({ ...prev, [level]: null }));
      setDeleteTarget(null);
    } catch (err) {
      setError(err.response?.data || "This category could not be deleted.");
      setDeleteTarget(null);
    }
  }

  const mainName = tree[1].find((c) => c.categoryId === selected[1])?.name;
  const subName = tree[2].find((c) => c.categoryId === selected[2])?.name;
  const term = search.trim().toLowerCase();

  function filtered(level) {
    if (!term) return tree[level];
    return tree[level].filter((c) => c.name.toLowerCase().includes(term));
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ mb: 3, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" } }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Avatar sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>
            <CategoryRoundedIcon />
          </Avatar>
          <Box>
            <Typography variant="h4">Categories</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage the marketplace category tree — departments, subcategories, and items.
            </Typography>
          </Box>
        </Stack>
        <Tooltip title="Refresh">
          <IconButton
            onClick={refreshAll}
            sx={{ border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
          >
            <RefreshRoundedIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {typeof error === "string" ? error : "Something went wrong."}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 2.5, p: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between" }}>
          <TextField
            placeholder="Search categories…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: 280 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch("")}>
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <Breadcrumbs separator="›" sx={{ fontSize: 14 }}>
            <Link
              component="button"
              underline={selected[1] ? "hover" : "none"}
              color={selected[1] ? "text.secondary" : "text.primary"}
              onClick={() => setSelected({ 1: null, 2: null })}
              sx={{ fontWeight: selected[1] ? 400 : 700, cursor: selected[1] ? "pointer" : "default" }}
            >
              All Categories
            </Link>
            {mainName && (
              <Link
                component="button"
                underline={selected[2] ? "hover" : "none"}
                color={selected[2] ? "text.secondary" : "text.primary"}
                onClick={() => setSelected((prev) => ({ ...prev, 2: null }))}
                sx={{ fontWeight: selected[2] ? 400 : 700, cursor: selected[2] ? "pointer" : "default" }}
              >
                {mainName}
              </Link>
            )}
            {subName && (
              <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
                {subName}
              </Typography>
            )}
          </Breadcrumbs>
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        {LEVELS.map((meta) => (
          <Grid item xs={12} md={4} key={meta.level}>
            <CategoryColumn
              meta={meta}
              items={filtered(meta.level)}
              hasItems={tree[meta.level].length > 0}
              loading={loading[meta.level]}
              enabled={meta.level === 1 || Boolean(selected[meta.level - 1])}
              selectedId={selected[meta.level]}
              onSelect={(id) => meta.level < 3 && setSelected((prev) => ({ ...prev, [meta.level]: id }))}
              onAdd={() => openCreateDialog(meta.level)}
              onEdit={(category) => openEditDialog(meta.level, category)}
              onDelete={(category) => setDeleteTarget({ level: meta.level, category })}
            />
          </Grid>
        ))}
      </Grid>

      {dialog && <CategoryDialog dialog={dialog} onClose={() => setDialog(null)} onSave={handleSave} />}

      {deleteTarget && (
        <Dialog open onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
          <DialogTitle>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "error.main", width: 36, height: 36 }}>
                <WarningAmberRoundedIcon fontSize="small" />
              </Avatar>
              <span>Delete {LEVELS[deleteTarget.level - 1].singular.toLowerCase()}?</span>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              You're about to delete <strong>{deleteTarget.category.name}</strong>. This cannot be undone.
              {deleteTarget.level < 3 && " Everything nested underneath it will be removed as well."}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

function CategoryColumn({ meta, items, hasItems, loading, enabled, selectedId, onSelect, onAdd, onEdit, onDelete }) {
  const Icon = meta.icon;

  return (
    <Paper variant="outlined" sx={{ height: "100%", borderRadius: 2, display: "flex", flexDirection: "column" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Icon fontSize="small" sx={{ color: "text.secondary" }} />
          <Typography variant="h6" sx={{ fontSize: 15 }}>
            {meta.title}
          </Typography>
          <Chip
            size="small"
            label={items.length}
            variant="outlined"
            sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
          />
        </Stack>
        <Tooltip title={enabled ? `Add ${meta.singular.toLowerCase()}` : "Select a parent first"}>
          <span>
            <IconButton size="small" color="primary" disabled={!enabled} onClick={onAdd}>
              <AddRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Divider />

      <Box sx={{ minHeight: 320, maxHeight: 480, overflowY: "auto", flex: 1 }}>
        {loading ? (
          <Stack spacing={1} sx={{ p: 1.5 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rounded" height={56} />
            ))}
          </Stack>
        ) : !hasItems ? (
          <Stack spacing={1} sx={{ alignItems: "center", justifyContent: "center", height: "100%", py: 6, px: 2 }}>
            <Icon sx={{ fontSize: 32, color: "text.disabled" }} />
            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "center" }}>
              {enabled ? meta.emptyText : "Select a parent first"}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
              {enabled ? meta.emptyHint : `Pick an item on the left to see its ${meta.title.toLowerCase()}.`}
            </Typography>
            {enabled && (
              <Button size="small" startIcon={<AddRoundedIcon />} onClick={onAdd} sx={{ mt: 0.5 }}>
                Add {meta.singular.toLowerCase()}
              </Button>
            )}
          </Stack>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No matches for this search.
          </Typography>
        ) : (
          <List disablePadding>
            {items.map((category) => (
              <ListItem
                key={category.categoryId}
                disablePadding
                divider
                sx={{
                  "&:hover .row-actions, &:focus-within .row-actions": { opacity: 1 },
                }}
                secondaryAction={
                  <Stack
                    direction="row"
                    spacing={0.25}
                    className="row-actions"
                    sx={{ opacity: 0.35, transition: "opacity .15s" }}
                  >
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => onEdit(category)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => onDelete(category)}>
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
              >
                <ListItemButton
                  selected={selectedId === category.categoryId}
                  onClick={() => onSelect(category.categoryId)}
                  sx={{ pr: 9, py: 1 }}
                >
                  <ListItemAvatar sx={{ minWidth: 48 }}>
                    <Avatar
                      variant="rounded"
                      src={category.imageUrl || undefined}
                      sx={{ width: 36, height: 36, bgcolor: "grey.100", color: "text.disabled" }}
                    >
                      <Icon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={category.name}
                    primaryTypographyProps={{ fontWeight: selectedId === category.categoryId ? 700 : 500 }}
                    secondary={
                      <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", mt: 0.25 }}>
                        <Typography variant="caption" color="text.secondary">
                          #{category.displayOrder}
                        </Typography>
                        {!category.isActive && (
                          <Chip
                            label="Inactive"
                            size="small"
                            color="default"
                            variant="outlined"
                            sx={{ height: 16, fontSize: 10, fontWeight: 600 }}
                          />
                        )}
                      </Stack>
                    }
                    secondaryTypographyProps={{ component: "div" }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Paper>
  );
}

function CategoryDialog({ dialog, onClose, onSave }) {
  const { level, parentName, category } = dialog;
  const meta = LEVELS[level - 1];
  const [name, setName] = useState(category?.name || "");
  const [imageUrl, setImageUrl] = useState(category?.imageUrl || "");
  const [displayOrder, setDisplayOrder] = useState(category?.displayOrder ?? 0);
  const [isActive, setIsActive] = useState(category?.isActive ?? true);
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true);
    try {
      await onSave({ name: name.trim(), imageUrl, displayOrder, isActive });
    } catch {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {category ? `Edit ${meta.singular}` : `New ${meta.singular}`}
        </Typography>
        {parentName && (
          <Typography variant="body2" color="text.secondary">
            in {parentName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar variant="rounded" src={imageUrl || undefined} sx={{ width: 64, height: 64, bgcolor: "grey.100" }}>
              <ImageOutlinedIcon color="disabled" />
            </Avatar>
            <TextField
              label="Image URL"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              helperText="Paste a hosted image link"
              fullWidth
              size="small"
            />
          </Stack>

          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth autoFocus />

          <TextField
            label="Display Order"
            type="number"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            helperText="Lower numbers appear first"
            fullWidth
          />

          {category && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 1.5, py: 1, borderRadius: 1.5, bgcolor: "action.hover" }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Active
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Inactive categories are hidden from customers
                </Typography>
              </Box>
              <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!name.trim() || saving} onClick={submit}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
